// FormContext.tsx
import React, { createContext, useContext, useRef, useState } from "react";
import { useImmer } from "use-immer";
import { DragEndEvent, DragOverEvent, DragStartEvent } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import { FieldTypeName } from "./fields";
import { nanoid } from 'nanoid';
import { z } from "zod";
import { JSONContent } from "@tiptap/core";

export interface FieldType {
  id: string;
  type: FieldTypeName;
  name: string;
  parent: null | string;
  question?: JSONContent | undefined | null;
  options?: { id: string; text: string }[];
  required?: boolean
}

export interface FieldResponse {
  fieldId: string;   // Unique ID for the field
  value: string;     // User input or selected option (always stored as string)
  fieldType: string; // Type of the field ('select', 'input', 'textarea', etc.)
}

export interface Form {
  fields: FieldType[];
  fieldResponses: FieldResponse[]; // Store responses for each field
}

const FieldResponseSchema = z.object({
  fieldId: z.string(),
  value: z.string().min(1, "Response cannot be empty"),  // Ensures non-empty string
  fieldType: z.string(),
});

interface FormContextProps {
  fieldResponses: FieldResponse[];
  updateFieldResponse: (fieldId: string, value: string, fieldType: string) => void;

  fields: FieldType[];
  activeSidebarField: FieldType | null
  activeField: FieldType | null
  sidebarFieldsRegenKey: number

  handleDragStart: (e: DragStartEvent) => void;
  handleDragOver: (e: DragOverEvent) => void;
  handleDragEnd: (e: DragEndEvent) => void;

  removeFieldById: (id: string) => void;
  setQuestionForField: (id: string, question: JSONContent) => void;

  addFieldOption: (fieldId: string, optionText?: string) => void;
  removeFieldOption: (fieldId: string, optionId: string) => void;
  updateOptionText: (fieldId: string, optionId: string, newText: string) => void;

  setFieldRequired: (fieldId: string, required:boolean) => void

  form_id: string | undefined
  setform_id: React.Dispatch<React.SetStateAction<string | undefined>>

  form_name: string
  setform_name: (name: string) => void

  form_description: string
  setform_description: (name: string) => void

  isFormComplete: () => boolean
  loadInitialFields: (fields:FieldType[]) => void
  loadInitialResponses: (responses:FieldResponse[]) => void
}

const FormContext = createContext<FormContextProps | undefined>(undefined);

function getData(prop: any) {
  return prop?.data?.current ?? {};
}

function createSpacer({ id, name }: { id: string, name:string }): FieldType {
  return {
    id,
    type: "spacer",
    name: name,
    parent: null,
  };
}

export const FormProvider = ({ children }: { children: React.ReactNode }) => {
    const [sidebarFieldsRegenKey, setSidebarFieldsRegenKey] = useState(Date.now());
    const spacerInsertedRef = useRef<boolean>(false);
    const currentDragFieldRef = useRef<FieldType | null>(null);
    const [activeSidebarField, setActiveSidebarField] = useState<FieldType | null>(null);
    const [activeField, setActiveField] = useState<FieldType | null>(null);
    const [form_id, setform_id] = useState<string | undefined>(undefined);
    const [form_name, setform_name] = useState<string>('');
    const [form_description, setform_description] = useState('');
    const [data, updateData] = useImmer<Form>({
      fields: [],
      fieldResponses: []
    });

    const { fields, fieldResponses } = data;

    const isFormComplete = (): boolean => {
      // Iterate through all fields to check for required ones

      for (const field of data.fields) {

        if (field.required) {
          // Find the corresponding response for this field
          const response = data.fieldResponses.find(
            (res) => res.fieldId === field.id
          );
    
          // If there's no response for a required field, return false
          if (!response) {
            return false;
          }
    
          // Validate the response using Zod schema
          const validationResult = FieldResponseSchema.safeParse(response);
          if (!validationResult.success) {
            return false;  // If validation fails, return false
          }
        }
      }
    
      // If all required fields are validated and complete, return true
      return true;
    };

    const updateFieldResponse = (fieldId: string, value: string, fieldType: string) => {
      updateData((draft) => {
        const existingField = draft.fieldResponses.find((r) => r.fieldId === fieldId);
        if (existingField) {
          existingField.value = value;
          existingField.fieldType = fieldType; // Store the field type too
        } else {
          draft.fieldResponses.push({ fieldId, value, fieldType });
        }
      });
    };

    const setFieldRequired = (id: string, required: boolean) => {
        updateData((draft) => {
          const field = draft.fields.find((f) => f.id === id);
          if (field) {
            field.required = required;
          }
        });
    };

    const setQuestionForField = (id: string, question: JSONContent) => {
      updateData(draft => {
        const field = draft.fields.find(f => f.id === id);
        if (field) {
          field.question = structuredClone(question);
        }
      });
    }

    const addFieldOption = (fieldId: string, optionText='') => {
        updateData((draft) => {
          const field = draft.fields.find((f) => f.id === fieldId);
          if (field && field.options) {
            field.options.push({ id: nanoid(), text: optionText });
          } else if (field) {
            field.options = [{ id: nanoid(), text: optionText }];
          }
        });
    };

    const removeFieldOption = (fieldId: string, optionId: string) => {
        updateData((draft) => {
          const field = draft.fields.find((f) => f.id === fieldId);
          if (field && field.options) {
            field.options = field.options.filter((option) => option.id !== optionId);
          }
        });
    };

    const updateOptionText = (fieldId: string, optionId: string, newText: string) => {
        updateData((draft) => {
          const field = draft.fields.find((f) => f.id === fieldId);
          if (field && field.options) {
            const option = field.options.find((opt) => opt.id === optionId);
            if (option) {
              option.text = newText;
            }
          }
        });
    };

    const cleanUp = () => {
        setActiveSidebarField(null);
        setActiveField(null);
        currentDragFieldRef.current = null;
        spacerInsertedRef.current = false;
    };
    
    const handleDragStart = (e: DragStartEvent) => {
        const { active } = e;
        const activeData = getData(active);
    
        // This is where the cloning starts.
        // We set up a ref to the field we're dragging
        // from the sidebar so that we can finish the clone
        // in the onDragEnd handler.
        if (activeData.fromSidebar) {
          const { field } = activeData;
          const { type } = field;
          setActiveSidebarField(field);
          // Create a new field that'll be added to the fields array
          // if we drag it over the canvas.
          currentDragFieldRef.current = {
            id: active.id.toString(),
            type,
            name: `${type}${fields.length + 1}`,
            parent: null,
          };
          return;
        }
    
        // We aren't creating a new element so go ahead and just insert the spacer
        // since this field already belongs to the canvas.
        const { field, index } = activeData;
    
        setActiveField(field);
        currentDragFieldRef.current = field;
        updateData((draft:any) => {
          draft.fields.splice(index, 1, createSpacer({ id: active.id.toString(), name:activeData.field.type }));
        });
    };
    
    const handleDragOver = (e: DragOverEvent) => {
        const { active, over } = e;
        const activeData = getData(active);

        if (activeData.fromSidebar) {
          const overData = getData(over);
    
          if (!spacerInsertedRef.current) {
            const spacer = createSpacer({
              id: active.id + "-spacer",
              name: activeData.field.type
            });
    
            updateData((draft:any) => {
              if (!draft.fields.length) {
                draft.fields.push(spacer);
              } else {
                const nextIndex =
                  overData.index > -1 ? overData.index : draft.fields.length;
    
                draft.fields.splice(nextIndex, 0, spacer);
              }
              spacerInsertedRef.current = true;
            });
          } else if (!over) {
            // This solves the issue where you could have a spacer handing out in the canvas if you drug
            // a sidebar item on and then off
            updateData((draft) => {
              draft.fields = draft.fields.filter((f) => f.type !== "spacer");
            });
            spacerInsertedRef.current = false;
          } else {
            // Since we're still technically dragging the sidebar draggable and not one of the sortable draggables
            // we need to make sure we're updating the spacer position to reflect where our drop will occur.
            // We find the spacer and then swap it with the over skipping the op if the two indexes are the same
            updateData((draft) => {
              const spacerIndex = draft.fields.findIndex(
                (f) => f.id === active.id + "-spacer"
              );
    
              const nextIndex =
                overData.index > -1 ? overData.index : draft.fields.length - 1;
    
              if (nextIndex === spacerIndex) {
                return;
              }
    
              draft.fields = arrayMove(draft.fields, spacerIndex, overData.index);
            });
          }
        }
    };
    
    const handleDragEnd = (e:DragEndEvent) => {
        const { over } = e;
    
        // We dropped outside of the over so clean up so we can start fresh.
        if (!over) {
          cleanUp();
          updateData((draft) => {
            draft.fields = draft.fields.filter((f) => f.type !== "spacer");
          });
          return;
        }
    
        // This is where we commit the clone.
        // We take the field from the this ref and replace the spacer we inserted.
        // Since the ref just holds a reference to a field that the context is aware of
        // we just swap out the spacer with the referenced field.
        let nextField = currentDragFieldRef.current;
    
        if (nextField) {
          const overData = getData(over);
    
          updateData((draft) => {
            const spacerIndex = draft.fields.findIndex((f) => f.type === "spacer");
            draft.fields.splice(spacerIndex, 1, nextField);
    
            draft.fields = arrayMove(
              draft.fields,
              spacerIndex,
              overData.index || 0
            );
          });
        }

        setSidebarFieldsRegenKey(Date.now());
        cleanUp();
    };

    const removeFieldById = (id: string) => {
        updateData((draft) => {
        draft.fields = draft.fields.filter((field) => field.id !== id);
        });
    };

    const loadInitialResponses = (responses:FieldResponse[]) => {
        updateData((draft) => {
          draft.fieldResponses = responses
        })
    }

    const loadInitialFields = (fields:FieldType[]) => {
      updateData((draft) => {
        draft.fields = fields
      })
    }

  return (
    <FormContext.Provider
        value={{ 
            fieldResponses, updateFieldResponse,

            fields, activeSidebarField, activeField, sidebarFieldsRegenKey,
            handleDragStart, handleDragOver, handleDragEnd, 
            removeFieldById, setQuestionForField, 
            addFieldOption, removeFieldOption, updateOptionText,
            setFieldRequired,

            form_id, setform_id,
            form_name, setform_name,
            form_description, setform_description,
            isFormComplete,
            loadInitialFields, loadInitialResponses
        }}
    >
      {children}
    </FormContext.Provider>
  );
};

export const useFormContext = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error("useFormContext must be used within a FormProvider");
  }
  return context;
};
